import { connectRouter, routerMiddleware, type RouterState } from 'connected-react-router';
import { createBrowserHistory, createMemoryHistory, type History } from 'history';
import { applyMiddleware, combineReducers, createStore, type Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { reducer as formReducer } from 'redux-form';
import reduxSaga from 'redux-saga';

import api, { apiV1, armorAPI, djangoApi } from '~/services/api';
import { gtmLocationChangeMiddleware } from '~/utils/gtm-middleware';
import { newRelic } from '~/utils/newRelic/newRelic';
import authRevokeMiddleware from '~/utils/redux/auth-revoke-middleware';
import * as batch from '~/utils/redux/batch';

import reducers, { type Reducers } from './reducers';
import sagas from './sagas';

// createMemoryHistory is for SSR. Probably never going to be needed.
// I'll leave it just in case.
const history = process.env.BROWSER ? createBrowserHistory() : createMemoryHistory();

export interface ApplicationState extends Reducers {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  // FIXME: Need to make this optional to avoid breaking stuff
  router?: RouterState;
}

const setupStore = (preloadedState: ApplicationState): Store<ApplicationState> => {
  const rootReducer = (history: History) =>
    combineReducers({
      ...reducers,
      form: formReducer,
      router: connectRouter(history),
    });

  const combinedReducers = rootReducer(history);

  const sagaMiddleware = reduxSaga({
    onError: (error, errorInfo) =>
      newRelic.noticeError('Uncaught error in redux saga', { error, errorInfo }),
  });

  const store = createStore(
    combinedReducers,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    preloadedState as any,
    composeWithDevTools(
      applyMiddleware(
        routerMiddleware(history),
        sagaMiddleware,
        gtmLocationChangeMiddleware,
        authRevokeMiddleware([api, armorAPI, djangoApi, apiV1]),
        batch.middleware,
      ),
      batch.enhancer,
    ),
  );

  sagas.startSagas(sagaMiddleware);

  // Hot reload reducers (requires Webpack or Browserify HMR to be enabled)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((module as any).hot) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (module as any).hot.accept('./reducers', () => {
      store.replaceReducer(rootReducer(history));
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (module as any).hot.accept('./sagas', () => {
      sagas.cancelSagas(store);
      require('./sagas').default.startSagas(sagaMiddleware);
    });
  }

  return store;
};

export default setupStore;
export { history };
